import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import t from 'ember-intl/helpers/t';
import { Section } from 'tio-ui/components/layout';
import Table from 'tio-ui/components/table';
import Popover from 'tio-ui/components/popover';
import { PhInfo } from 'ember-phosphor-icons';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
import { REQUESTING_APP } from '../../utils/strategy-finder-0726';

// Federal Student Aid (U.S. Dept. of Education) repayment plans page.
const DOE_REPAYMENT_PLANS_URL = 'https://studentaid.gov/manage-loans/repayment/plans';

interface UsageHistorySignature {
  Args: {
    recommendations: RecommendationModel[];
    // Host app's results route. Both this tool's runs and the legacy Strategy
    // Finder runs open here (it loads any recommendation by id); supplied by the
    // host since this component is shared across apps.
    resultsRoute: string;
  };
  Element: HTMLElement;
}

const UsageHistory: TOC<UsageHistorySignature> = <template>
  <Section data-test-sf-0726-history ...attributes>
    <:header>{{t "strategy_finder_0726.history.title"}}</:header>
    <:body>
      <Table @isLoading={{false}}>
        <:thead as |options|>
          <options.tr>
            <options.th>{{t "strategy_finder_0726.history.date"}}</options.th>
            <options.th>{{t "strategy_finder_0726.history.tool"}}</options.th>
            <options.th>{{t "strategy_finder_0726.history.view"}}</options.th>
          </options.tr>
        </:thead>
        <:tbody as |options|>
          {{#each @recommendations as |rec|}}
            <options.tr data-test-sf-0726-history-row={{rec.requestingApp}}>
              <options.td>
                {{safeFormatDate rec.createdAt month="short" day="numeric" year="numeric"}}
              </options.td>
              <options.td>
                {{#if (eq rec.requestingApp REQUESTING_APP)}}
                  {{t "strategy_finder_0726.history.tool_sf_0726"}}
                {{else}}
                  <span class="tio-row gap-1 items-center">
                    {{t "strategy_finder_0726.history.tool_strategy_finder"}}
                    <Popover @triggerMode="hover">
                      <:trigger as |pop|>
                        <span {{pop.register}} {{pop.hoverHandlers}} data-test-sf-0726-history-info>
                          <PhInfo class="tio-fg-secondary" @size={{16}} />
                        </span>
                      </:trigger>
                      <:body>
                        <p class="text-sm max-w-xs">
                          {{t "strategy_finder_0726.history.legacy_info"}}
                          <a
                            href={{DOE_REPAYMENT_PLANS_URL}}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="tio-copy"
                          >
                            {{t "strategy_finder_0726.history.legacy_info_link"}}
                          </a>
                        </p>
                      </:body>
                    </Popover>
                  </span>
                {{/if}}
              </options.td>
              <options.td>
                <LinkTo @route={{@resultsRoute}} @model={{rec.id}} class="tio-copy">
                  {{t "strategy_finder_0726.history.view"}}
                </LinkTo>
              </options.td>
            </options.tr>
          {{/each}}
        </:tbody>
      </Table>
    </:body>
  </Section>
</template>;

export default UsageHistory;
